<template>
    <b-modal
        id="personal-goal-overview-modal"
        v-model="modal"
        v-if="personalGoal"
        modal-class="apprentice-default-modal personal-goal-overview-modal"
        :title="$t('personal_goals.learning_goal') + `: ${personalGoal.name}`"
        header-class="personal-goal-overview-modal-header"
        footer-class="personal-goal-overview-modal-footer"
        ok-only
        :ok-title="$t('common.close')"
        ok-variant="primary"
        size="lg"
        :no-enforce-focus="true">
        <template #modal-header="{ close }">
            <div :class="['modal-header-content', personalGoal.badges.length > 0 && 'has-badge']">
                <h5 class="modal-header-title">
                    {{ $t('personal_goals.learning_goal') + `: ${personalGoal.name}` }}

                    <div
                        class="badge-earned"
                        v-if="personalGoal.badges.length > 0">
                        <img
                            class="badge-image"
                            :src="personalGoal.badges[0].file.public_path"
                            alt="Badge" />
                    </div>
                </h5>

                <b-button
                    class="btn-times close"
                    variant="primary-icon-only"
                    @click="close()">
                </b-button>
            </div>
        </template>

        <div class="task-overview">
            <design2021-apprentice-data-table
                class="personal-goal-tasks-overview-table"
                :items="personalGoal.tasks"
                :fields="fields"
                nosearch
                :show-empty="true">
                <template #options>
                    <b-button
                        class="btn-show btn-skill-tree-collapse"
                        v-b-toggle.skillTreeCollapse
                        v-if="filteredSkilltrees.length > 0"
                        variant="outline-primary"
                        @click="setSkillTreeContextMenuPosition">
                        {{ $t('personal_goals.show_skills') }}
                    </b-button>

                    <b-collapse
                        class="skill-tree-collapse"
                        id="skillTreeCollapse">
                        <ul class="list-unstyled skill-tree">
                            <li
                                v-for="skillTree of filteredSkilltrees"
                                :key="skillTree.id">
                                <ul class="personal-goal-skills list-unstyled">
                                    <div class="skill-heading">
                                        {{ skillTree.name }}
                                    </div>
                                    <li
                                        class="skill-item"
                                        v-for="skill of skillTree.skills"
                                        :key="skill.id">
                                        {{ skill.name }}
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </b-collapse>

                    <div
                        class="add-badge"
                        v-if="
                            ($authUser.isAdmin() || $authUser.isTeacher()) &&
                            areAllTasksCompleted() &&
                            personalGoal.archived === 0 &&
                            badgesIn.length > 0
                        ">
                        <b-button
                            class="btn-add btn-add-badge"
                            variant="primary"
                            @click="showBadgesModal">
                            {{ $t('personal_goals.add_badge') }}
                        </b-button>
                    </div>

                    <b-button
                        class="btn-address-card btn-add-to-timeline"
                        v-if="teacherAccess === 0 && areAllTasksCompleted() && personalGoal.archived === 0"
                        ref="btnShowTimelinModal"
                        variant="outline-primary"
                        @click="showAddToTimelineModal">
                        {{ $t('assignment.add_to_timeline') }}
                    </b-button>

                    <b-button
                        class="btn-edit btn-edit-personal-goal"
                        v-if="teacherAccess === 0 && personalGoal.archived === 0"
                        variant="outline-primary"
                        @click="editPersonalGoal">
                        {{ $t('common.add_learning_goal') }}
                    </b-button>

                    <b-button
                        class="btn-add btn-add-personal-goal-task"
                        v-if="teacherAccess === 0 && personalGoal.archived === 0"
                        variant="primary"
                        @click="addPersonalGoalTask">
                        {{ $t('personal_goals.add_task') }}
                    </b-button>
                </template>

                <template #cell(status)="row">
                    <div class="status">
                        <i
                            class="icon status-icon fad"
                            v-b-tooltip.hover
                            :title="
                                row.item.checked === 1
                                    ? $t('personal_goals.task_status_completed')
                                    : $t('personal_goals.task_status_open')
                            "
                            :class="
                                row.item.checked === 1 ? 'fa-check-circle text-success' : 'fa-times-circle text-danger'
                            ">
                        </i>
                    </div>
                </template>

                <template #cell(deadline)="row">
                    <div
                        class="deadline"
                        v-if="
                            row.item.personal_goal_task_deadline !== null &&
                            typeof row.item.personal_goal_task_deadline === 'object' &&
                            row.item.personal_goal_task_deadline.agenda_item !== null &&
                            typeof row.item.personal_goal_task_deadline.agenda_item
                        ">
                        {{ row.item.personal_goal_task_deadline.agenda_item.date | date_named_month_short }}
                    </div>
                </template>

                <template #cell(actions)="row">
                    <div
                        class="management-actions"
                        v-if="personalGoal.archived === 0">
                        <b-button
                            class="btn-show btn-show-personal-goal-task"
                            v-b-tooltip.hover
                            variant="primary-icon-only"
                            :title="$t('personal_goals.view_task')"
                            @click="showPersonalGoalTask(row.item.id)">
                        </b-button>
                        <b-button
                            class="btn-edit btn-edit-personal-goal-task"
                            v-b-tooltip.hover
                            v-if="teacherAccess === 0"
                            variant="primary-icon-only"
                            :title="$t('personal_goals.edit_task')"
                            @click="showPersonalGoalTaskEdit(row.item)">
                        </b-button>
                        <b-button
                            class="btn-remove btn-remove-personal-goal-task"
                            v-b-tooltip.hover
                            v-if="teacherAccess === 0"
                            variant="danger-icon-only"
                            :title="$t('personal_goals.delete_task')"
                            @click="removePersonalGoalTask(row.item)">
                        </b-button>
                    </div>
                </template>

                <template #empty="scope">
                    <div class="not-found with-header">
                        <div class="icon">
                            <i class="fas fa-exclamation-circle"></i>
                        </div>
                        <div class="alert-notice">{{ $t('personal_goals.this_learning_goal_has_no_tasks') }}</div>
                    </div>
                </template>
            </design2021-apprentice-data-table>

            <design-2021-personal-goal-add-task-modal-component
                ref="add-personal-goal-task"
                @task-added="addTask">
            </design-2021-personal-goal-add-task-modal-component>

            <design-2021-personal-goal-task-overview-modal-component
                ref="personal-goal-task-overview"
                :teacher-access="teacherAccess"
                @update-task-status="updatedTaskStatus">
            </design-2021-personal-goal-task-overview-modal-component>

            <design-2021-personal-goal-edit-task-modal-component
                ref="edit-personal-goal-task"
                @personal-goal-task-updated="editPersonalGoalTask">
            </design-2021-personal-goal-edit-task-modal-component>

            <design-2021-personal-goal-edit-personal-goal-modal-component
                :personal-goal-in="personalGoal"
                :skill-trees-in="skillTreesIn"
                ref="edit-personal-goal"
                @personal-goal-updated="updatePersonalGoal">
            </design-2021-personal-goal-edit-personal-goal-modal-component>

            <design-2021-apprentice-badges-overview-component
                :badges-in="badgesIn"
                ref="personal-goal-badges-overview">
            </design-2021-apprentice-badges-overview-component>
        </div>
    </b-modal>
</template>

<script>
import { ords } from '../../../app';
import Design2021ApprenticeDataTable from '../../common/design-2021-apprentice-data-table.vue';
import { nextTick } from 'vue';
import moment from 'moment/moment';

export default {
    name: 'design-2021-personal-goal-overview-modal-component',
    components: { Design2021ApprenticeDataTable },
    props: {
        teacherAccess: {
            type: Boolean,
            required: false,
            default: false,
        },

        badgesIn: {
            type: Array,
            required: true,
            default: () => [],
        },

        skillTreesIn: {
            type: Array,
            required: true,
            default: () => [],
        },
    },
    data() {
        return {
            modal: false,
            skills: [],
            fields: [
                {
                    key: 'name',
                    label: this.$t('common.name'),
                },
                {
                    key: 'deadline',
                    label: this.$t('common.deadline'),
                },
                {
                    key: 'status',
                    label: this.$t('common.status'),
                },
                {
                    key: 'actions',
                    label: this.$t('common.actions'),
                },
            ],
            personalGoal: '',
            tasks: [],
            taskStatusUpdated: false,
        };
    },

    methods: {
        setSkillTreeContextMenuPosition() {
            const options = document.getElementsByClassName('apprentice-data-table-actions');
            const collapseButton = document.getElementsByClassName('btn-skill-tree-collapse');
            const collapseSkillTreeMenu = document.getElementsByClassName('skill-tree-collapse');

            if (options && collapseButton[0].attributes[4].value === 'false') {
                let positionLeft = options[0].offsetWidth - collapseButton[0].offsetLeft;
                let positionTop = collapseButton[0].offsetTop + collapseButton[0].offsetHeight + 4;

                if (positionLeft < 350 && options[0].offsetWidth > 350) {
                    positionLeft = options[0].offsetWidth - 350;
                } else {
                    positionLeft = collapseButton[0].offsetLeft;
                }

                collapseSkillTreeMenu[0].style.left = positionLeft + 'px';
                collapseSkillTreeMenu[0].style.top = positionTop + 'px';
            }
        },

        editPersonalGoal() {
            this.$refs['edit-personal-goal'].show();
        },

        editPersonalGoalTask(updatedTask) {
            for (let i = 0; i < this.personalGoal.tasks.length; i++) {
                if (this.personalGoal.tasks[i].id === updatedTask.id) {
                    this.personalGoal.tasks[i].update(updatedTask);

                    if (
                        updatedTask.personal_goal_task_deadline &&
                        updatedTask.personal_goal_task_deadline.agenda_item
                    ) {
                        if (
                            this.personalGoal.tasks[i].personal_goal_task_deadline &&
                            this.personalGoal.tasks[i].personal_goal_task_deadline.agenda_item
                        ) {
                            this.personalGoal.tasks[i].personal_goal_task_deadline.agenda_item.update(
                                updatedTask.personal_goal_task_deadline.agenda_item,
                            );
                            this.personalGoal.tasks[i].personal_goal_task_deadline.update(
                                updatedTask.personal_goal_task_deadline,
                            );
                        } else {
                            this.personalGoal.tasks[i].addRelation(
                                'personal_goal_task_deadline',
                                updatedTask.personal_goal_task_deadline,
                            );
                        }
                    } else {
                        if (
                            this.personalGoal.tasks[i].personal_goal_task_deadline &&
                            this.personalGoal.tasks[i].personal_goal_task_deadline.agenda_item
                        ) {
                            this.personalGoal.tasks[i].personal_goal_task_deadline.agenda_item.remove();
                            this.personalGoal.tasks[i].personal_goal_task_deadline.remove();
                        } else if (this.personalGoal.tasks[i].personal_goal_task_deadline !== null) {
                            this.personalGoal.tasks[i].personal_goal_task_deadline.remove();
                        }
                    }
                }
            }
        },

        showPersonalGoalTask(taskId) {
            const task = this.personalGoal.tasks.find((task) => task.id === taskId);
            this.$refs['personal-goal-task-overview'].show(task);
        },

        addPersonalGoalTask() {
            this.$refs['add-personal-goal-task'].show(this.personalGoal);
        },

        showPersonalGoalTaskEdit(task) {
            const existingTask = this.personalGoal.tasks.find((pg_task) => pg_task.id === task.id);
            this.$refs['edit-personal-goal-task'].show(existingTask);
        },

        removePersonalGoalTask(personalGoalTask) {
            const task = this.personalGoal.tasks.find((task) => task.id === personalGoalTask.id);

            this.alert.saving();
            this.axios.delete(this.routes.personalGoalTaskRemove(this.personalGoal.id, personalGoalTask.id)).then(
                (response) => {
                    this.alert.success();
                    task.remove();
                    this.$emit('personal-goal-tasks-removed', personalGoalTask);
                },

                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        show(goal) {
            ords.use('personalGoal')
                .with('tasks', (taskCallback) => {
                    taskCallback.with('files');
                    taskCallback.with('personal_goal_task_deadline', (deadlineCallback) => {
                        deadlineCallback.with('agenda_item');
                    });
                })
                .with('skills')
                .with('badges', (badgesCallback) => {
                    badgesCallback.with('file');
                })
                .find(goal.id)
                .subscribe((personalgoal) => {
                    this.personalGoal = personalgoal;
                });

            this.modal = true;
        },

        updatePersonalGoal(updatedPersonalGoal) {
            this.personalGoal.update(updatedPersonalGoal);
            this.$emit('personal-goal-updated', updatedPersonalGoal);
        },

        addTask(task) {
            ords.hold();
            this.personalGoal.attach(this.personalGoal.id, 'tasks', task.id);
            ords.continue();
        },

        updatedTaskStatus(task) {
            for (let i = 0; i < this.personalGoal.tasks.length; i++) {
                if (this.personalGoal.tasks[i].id === task.id) {
                    this.personalGoal.tasks[i].update(task);
                    this.personalGoal.tasks[i].addRelation('personal_goal_task_deadline', task.file);
                }
            }

            if (task.checked === 1) {
                this.$emit('update-progress-bar', task);
            }

            this.taskStatusUpdated = true;

            if (this.areAllTasksCompleted()) {
                // Due to the closing task overview modal, the confirmation handle needs a little delay before executing!
                setTimeout(() => {
                    this.copyToTimelineConfirmation();
                }, 2000);
            }
        },

        areAllTasksCompleted() {
            let status = false;

            if (this.personalGoal.tasks.length > 0) {
                status = true;

                if (this.personalGoal.tasks.find((task) => task.checked === 0)) {
                    status = false;
                }
            }

            return status;
        },

        async copyToTimelineConfirmation() {
            await this.alert
                .confirm(
                    this.$t('personal_goals.share_to_timeline'),
                    this.$t('personal_goals.confirm_share_to_timeline'),
                    this.$t('personal_goals.yes_share'),
                )
                .then((confirm) => {
                    if (!confirm) {
                        return;
                    }

                    this.showAddToTimelineModal();
                    this.alert.close();
                });
        },

        showBadgesModal() {
            let badge = {};
            if (this.personalGoal.badges.length > 0) {
                badge = this.personalGoal.badges[0];
            }
            this.$refs['personal-goal-badges-overview'].show(this.personalGoal);
        },

        showAddToTimelineModal() {
            const tasks = `${this.personalGoal.tasks
                .map(
                    (task) =>
                        `<div><h3>${this.$t('personal_goals.task')}: ${task.name}</h3>${task.reflection}<br>&nbsp;</div>`,
                )
                .join('')}`;

            const files = this.personalGoal.tasks.map((taskFile) => {
                return {
                    type: 'file',
                    file: {
                        id: taskFile.id,
                        name: taskFile.file.full_name,
                        extension: taskFile.file.extension,
                        type: taskFile.file.extension,
                    },
                };
            });

            const profileCard = {
                title: `Leerdoel: ${this.personalGoal.name}`,
                description: tasks,
                date: moment(new Date()).format('YYYY-MM-DD'),
                category_id: null,
                attachments: files,
            };

            this.$emit('show-profile-card-from-personal-goal-modal', profileCard);
        },
    },

    computed: {
        filteredSkilltrees() {
            let filteredSkills = Array.from(this.skillTreesIn).filter((tree) => tree.skills.length > 0);

            if (this.personalGoal.skills.length > 0) {
                const skillsIds = this.personalGoal.skills.map((skill) => skill.id);

                filteredSkills = filteredSkills
                    .map((skillTree) => {
                        let tempSkillsTree = Object.assign({}, skillTree);
                        tempSkillsTree.skills = tempSkillsTree.skills.filter((skill) => skillsIds.includes(skill.id));
                        return tempSkillsTree;
                    })
                    .filter((tree) => tree.skills.length > 0);
            }

            return filteredSkills;
        },
    },
};
</script>

<style scoped lang="scss">
.modal-header-content {
    display: flex;
    flex: 1;
    flex-wrap: wrap;
    justify-content: space-evenly;
    align-items: flex-start;

    .modal-header-title {
        flex: 1 0 calc(100% - 70px);
        margin: 0;
        position: relative;
    }

    .btn-close-modal {
        font-size: 1.5rem;
    }

    &.has-badge {
        margin-bottom: 80px;
    }
}

.badge-earned {
    left: calc(50% - 40px);
    position: absolute;
}

.personal-goal-tasks-overview-table ::v-deep {
    .options {
        column-gap: map-get($grid-gutter-spacing, 'fifteen');
        position: relative;
        flex-grow: 1;
    }

    .status {
        .icon {
            font-size: 1.3125rem;
        }
    }
}

.skills-list {
    align-self: flex-start;
    margin-top: map-get($grid-gutter-spacing, 'fifteen');

    .empty-skill-list {
        color: $text-muted-color;
        font-weight: $font-weight-medium;
    }
}

.btn-skill-tree-collapse {
    line-height: 1;
}

.skill-tree-collapse {
    position: absolute;
    inset: 44px 0 0;
    background-color: $white;
    max-width: min(100%, 350px);
    z-index: 1025;

    ul {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
    }

    li {
        width: 100%;

        > ul li {
            width: calc(100% - 30px);
        }
    }

    .skill-tree {
        background-color: $white;
        border: 1px solid $border-color;
        border-radius: 0.25rem;

        li:not(:last-child) {
            margin-bottom: map-get($grid-gutter-spacing, 'four');
        }
    }

    .personal-goal-skills {
        .skill-heading {
            background-color: rgba($primary, 55%);
            color: $white;
            padding: map-get($grid-gutter-spacing, 'five') map-get($grid-gutter-spacing, 'five')
                map-get($grid-gutter-spacing, 'five') map-get($grid-gutter-spacing, 'fifteen');
            width: 100%;
        }

        .skill-item {
            margin-left: $grid-gutter-width;
            padding: map-get($grid-gutter-spacing, 'four');
        }
    }
}

.skills-and-badges {
    display: flex;
    flex-direction: column-reverse;
    column-gap: map-get($grid-gutter-spacing, 'sixty');
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: map-get($grid-gutter-spacing, 'forty-two');
}

.skills {
    flex: 1 0 100%;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    column-gap: map-get($grid-gutter-spacing, 'ten');
    row-gap: map-get($grid-gutter-spacing, 'ten');
    margin-top: map-get($grid-gutter-spacing, 'five');
}

.add-badge {
    width: 100%;
    margin-bottom: map-get($grid-gutter-spacing, 'fifteen');

    .btn-add-badge {
        width: inherit;
    }
}

.skill-badge {
    font-size: 0.6875rem;
}

.badge-image {
    width: 95%;
    height: 95%;
    object-position: center;
    object-fit: contain;
}

@media (min-width: map-get($grid-breakpoints, 'sm')) {
    .skills-and-badges {
        flex-direction: row;
        align-items: center;
    }

    .add-badge {
        flex: 0 0 auto;
        width: auto;
        margin-bottom: 0;
    }

    .personal-goal-tasks-overview-table ::v-deep {
        .options {
            flex-wrap: wrap;
            row-gap: map-get($grid-gutter-spacing, 'fifteen');
            justify-content: flex-end;
        }
    }
}
</style>
